import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../hooks/useCurrency';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  category?: string;
  image: string;
  rating?: number;
  featured?: boolean;
  soldOut?: boolean;
}

interface ProductQuickViewProps {
  product: Product | null;
  isOpen: boolean;
  onClose: () => void;
  onAddToCart: (product: Product) => void;
}

interface Review {
  id: string;
  userName: string;
  rating: number;
  comment: string;
  date: string;
  verified: boolean;
}

const ProductQuickView = ({ product, isOpen, onClose, onAddToCart }: ProductQuickViewProps) => {
  const { t } = useTranslation();
  const { formatPrice, currency, setCurrency } = useCurrency();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState<'description' | 'benefits' | 'contents' | 'production' | 'reviews'>('description');
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([
    {
      id: '1',
      userName: 'Sarah M.',
      rating: 5,
      comment: 'Excellent quality! Highly recommend.',
      date: '2025-01-15',
      verified: true
    },
    {
      id: '2',
      userName: 'John D.',
      rating: 5,
      comment: 'Amazing product! Fresh and delicious.',
      date: '2025-01-10',
      verified: true
    },
    {
      id: '3',
      userName: 'Emily R.',
      rating: 4,
      comment: 'Very good quality, will order again.',
      date: '2025-01-05',
      verified: true
    }
  ]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({
    userName: '',
    rating: 5,
    comment: ''
  });

  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'IRR', symbol: '﷼', name: 'Iranian Rial' },
    { code: 'AMD', symbol: '֏', name: 'Armenian Dram' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble' }
  ];

  // Reset state when product changes or modal opens
  useEffect(() => {
    if (isOpen && product) {
      setQuantity(1);
      setSelectedImage(0);
      setActiveTab('description');
      setShowReviewForm(false);
      setNewReview({ userName: '', rating: 5, comment: '' });
    }
  }, [isOpen, product]);

  // Get product emoji based on category
  const getProductEmoji = (category?: string) => {
    const emojiMap: Record<string, string[]> = {
      nuts: ['🥜', '🌰', '🫘'],
      driedfruits: ['🍇', '🍑', '🥭', '🍊', '🫐'],
      sweets: ['🍬', '🍭', '🧁', '🍰', '🎂'],
      pickles: ['🥒', '🫒', '🧄'],
      jams: ['🍓', '🍊', '🫐', '🍯']
    };
    
    const categoryKey = category?.toLowerCase().replace(/\s+/g, '') || 'nuts';
    return emojiMap[categoryKey] || emojiMap.nuts;
  };

  // Falling product emojis based on category
  const fallingEmojis = useMemo(() => {
    if (!product) return [];
    
    const categoryEmojis: Record<string, string[]> = {
      'Nuts': ['🥜', '🌰', '🫘'],
      'Dried Fruits': ['🍇', '🍑', '🥭', '🍊', '🫐'],
      'Sweets': ['🍬', '🍭', '🧁', '🍰', '🎂'],
      'Pickles': ['🥒', '🫒', '🧄'],
      'Jams': ['🍓', '🍊', '🫐', '🍯']
    };

    const emojis = categoryEmojis[product.category] || ['🎁', '✨', '🌟'];
    const isMobile = window.innerWidth < 768;
    const emojiCount = isMobile ? 6 : 12;
    
    return Array.from({ length: emojiCount }, (_, i) => ({
      emoji: emojis[i % emojis.length],
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 5}s`,
      duration: `${8 + Math.random() * 8}s`,
      size: isMobile ? `${15 + Math.random() * 15}px` : `${20 + Math.random() * 20}px`
    }));
  }, [product]);

  const handleSubmitReview = () => {
    if (!newReview.userName.trim() || !newReview.comment.trim()) {
      alert('Please fill in all fields');
      return;
    }

    const review: Review = {
      id: Date.now().toString(),
      userName: newReview.userName,
      rating: newReview.rating,
      comment: newReview.comment,
      date: new Date().toISOString().split('T')[0],
      verified: false
    };

    setReviews([review, ...reviews]);
    setNewReview({ userName: '', rating: 5, comment: '' });
    setShowReviewForm(false);
    
    // Show success message
    const successMsg = document.createElement('div');
    successMsg.className = 'fixed top-4 right-4 bg-emerald-600 text-white px-6 py-3 rounded-xl shadow-2xl z-[100] animate-slide-down';
    successMsg.innerHTML = '<i class="ri-check-line mr-2"></i>Review submitted successfully!';
    document.body.appendChild(successMsg);
    setTimeout(() => successMsg.remove(), 3000);
  };

  if (!isOpen || !product) return null;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      onAddToCart(product);
    }
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Generate multiple product images (in real scenario, these would come from product data)
  const productImages = [
    product.image,
    product.image,
    product.image
  ];

  // Product-specific data based on category
  const getProductBenefits = () => {
    const benefits = {
      nuts: [
        'Rich in healthy fats and omega-3 fatty acids',
        'Excellent source of protein and fiber',
        'Supports heart health and brain function',
        'Helps maintain healthy cholesterol levels',
        'Provides essential vitamins and minerals',
        'Natural energy booster for active lifestyles'
      ],
      driedFruits: [
        'High in natural antioxidants and vitamins',
        'Excellent source of dietary fiber',
        'Supports digestive health naturally',
        'Provides quick and sustained energy',
        'Rich in essential minerals like iron and potassium',
        'No added sugars or preservatives'
      ],
      sweets: [
        'Made with traditional authentic recipes',
        'Uses premium natural ingredients',
        'Perfect for special occasions and gifts',
        'Unique Middle Eastern flavors',
        'Handcrafted with care and expertise',
        'Cultural heritage in every bite'
      ],
      pickles: [
        'Rich in probiotics for gut health',
        'Low in calories and fat-free',
        'Enhances digestion naturally',
        'Traditional fermentation process',
        'No artificial preservatives',
        'Adds flavor to any meal'
      ],
      jams: [
        'Made from fresh, ripe fruits',
        'No artificial colors or flavors',
        'Perfect for breakfast and desserts',
        'Rich in natural fruit vitamins',
        'Traditional homemade recipes',
        'Versatile for cooking and baking'
      ]
    };
    return benefits[product.category?.toLowerCase() as keyof typeof benefits] || benefits.nuts;
  };

  const getProductContents = () => {
    const contents = {
      nuts: [
        'Premium selected nuts',
        'Natural sea salt (for salted varieties)',
        'No artificial additives',
        'No preservatives',
        'Gluten-free',
        'Vegan-friendly'
      ],
      driedFruits: [
        '100% natural dried fruits',
        'No added sugar',
        'No sulfur dioxide',
        'No artificial colors',
        'No preservatives',
        'Naturally sun-dried'
      ],
      sweets: [
        'Premium quality flour',
        'Natural honey and sugar',
        'Fresh butter and eggs',
        'Traditional spices',
        'Pistachios and almonds',
        'Rose water (in select varieties)'
      ],
      pickles: [
        'Fresh vegetables',
        'Natural brine solution',
        'Sea salt',
        'Vinegar',
        'Traditional spices',
        'No artificial preservatives'
      ],
      jams: [
        'Fresh seasonal fruits (60%+)',
        'Natural cane sugar',
        'Lemon juice',
        'Pectin (natural)',
        'No artificial flavors',
        'No preservatives'
      ]
    };
    return contents[product.category?.toLowerCase() as keyof typeof contents] || contents.nuts;
  };

  const getProductionConditions = () => {
    return [
      'Produced in certified food-safe facilities',
      'Strict quality control at every stage',
      'Temperature-controlled storage',
      'Hygienic processing environment',
      'Regular safety inspections',
      'Meets international food safety standards',
      'Sustainable sourcing practices',
      'Eco-friendly packaging materials'
    ];
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 md:inset-4 lg:inset-8 z-50 flex items-center justify-center p-0 md:p-4 transition-all duration-300 ${
          isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        }`}
      >
        <div className="bg-white w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl md:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
          {/* Header - Mobile optimized */}
          <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 flex-shrink-0">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Quick View
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 md:w-12 md:h-12 bg-white hover:bg-gray-100 rounded-xl md:rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer group shadow-md touch-manipulation"
              aria-label="Close"
            >
              <i className="ri-close-line text-2xl md:text-3xl text-gray-700 group-hover:rotate-90 transition-transform duration-300"></i>
            </button>
          </div>

          {/* Content - Scrollable on mobile */}
          <div className="flex-1 overflow-y-auto p-8">
            {/* Sold Out Banner - Top of Quick View */}
            {product.soldOut && (
              <div className="mb-6 bg-gradient-to-r from-red-500 via-rose-500 to-red-600 text-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center justify-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <i className="ri-close-circle-fill text-4xl"></i>
                  </div>
                  <div>
                    <div className="text-3xl font-bold tracking-wide uppercase">
                      {t('soldOut')}
                    </div>
                    <div className="text-lg font-medium opacity-90 mt-1">
                      {t('notifyWhenAvailable')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-8 p-4 md:p-8">
              {/* Left: Images */}
              <div className="space-y-3 md:space-y-4 mb-6 md:mb-0">
                {/* Main Image */}
                <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl md:rounded-2xl overflow-hidden">
                  <img
                    src={product.images?.[selectedImage] || product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {product.featured && (
                    <div className="absolute top-3 md:top-4 left-3 md:left-4 px-3 py-1.5 md:px-4 md:py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs md:text-sm font-bold rounded-lg md:rounded-xl shadow-lg animate-pulse-glow flex items-center gap-1 md:gap-2">
                      <i className="ri-star-fill text-sm md:text-base"></i>
                      Featured
                    </div>
                  )}
                </div>

                {/* Thumbnail Images */}
                {product.images && product.images.length > 1 && (
                  <div className="grid grid-cols-4 gap-2 md:gap-3">
                    {product.images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`aspect-square rounded-lg md:rounded-xl overflow-hidden transition-all duration-300 cursor-pointer touch-manipulation ${
                          selectedImage === index
                            ? 'ring-4 ring-emerald-500 scale-105'
                            : 'ring-2 ring-gray-200 hover:ring-emerald-300'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`${product.name} ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Product Info */}
              <div className="space-y-4 md:space-y-6">
                {/* Category Badge */}
                {product.category && (
                  <div className="inline-block px-3 py-1.5 md:px-4 md:py-2 bg-emerald-100 text-emerald-700 text-xs md:text-sm font-bold rounded-lg md:rounded-xl">
                    {product.category}
                  </div>
                )}

                {/* Product Name */}
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {product.name}
                </h3>

                {/* Rating */}
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex items-center gap-0.5 md:gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`${
                          i < Math.floor(product.rating || 5) ? 'ri-star-fill' : 'ri-star-line'
                        } text-amber-500 text-base md:text-lg`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-sm md:text-base text-gray-600 font-semibold">
                    {product.rating || 5} ({reviews.length} reviews)
                  </span>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-3 md:gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl md:text-4xl lg:text-5xl font-bold text-emerald-600">
                      {formatPrice(product.price)}
                    </span>
                    
                    {/* Currency Selector */}
                    <div className="relative">
                      <button
                        onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                        className="flex items-center gap-1 px-2 md:px-3 py-1 md:py-1.5 bg-gray-100 hover:bg-emerald-50 rounded-lg transition-all duration-300 cursor-pointer touch-manipulation"
                        aria-label="Change currency"
                      >
                        <span className="text-xs md:text-sm font-semibold text-gray-700">{currency.code}</span>
                        <i className={`ri-arrow-down-s-line text-sm md:text-base text-gray-600 transition-transform duration-300 ${showCurrencyMenu ? 'rotate-180' : ''}`}></i>
                      </button>
                      
                      {/* Currency Dropdown */}
                      {showCurrencyMenu && (
                        <>
                          <div 
                            className="fixed inset-0 z-40" 
                            onClick={() => setShowCurrencyMenu(false)}
                          ></div>
                          <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 min-w-[180px] md:min-w-[200px]">
                            {currencies.map((curr) => (
                              <button
                                key={curr.code}
                                onClick={() => {
                                  setCurrency(curr.code);
                                  setShowCurrencyMenu(false);
                                }}
                                className={`w-full px-3 md:px-4 py-2 md:py-2.5 text-left hover:bg-emerald-50 transition-colors duration-200 flex items-center justify-between cursor-pointer touch-manipulation ${
                                  currency.code === curr.code ? 'bg-emerald-50 text-emerald-600 font-semibold' : 'text-gray-700'
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <span className="text-base md:text-lg">{curr.symbol}</span>
                                  <span className="text-xs md:text-sm">{curr.name}</span>
                                </span>
                                {currency.code === curr.code && (
                                  <i className="ri-check-line text-emerald-600 text-base md:text-lg"></i>
                                )}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {product.originalPrice && (
                    <span className="text-lg md:text-xl lg:text-2xl text-gray-400 line-through">
                      {formatPrice(product.originalPrice)}
                    </span>
                  )}
                </div>

                {/* Tabs - Mobile optimized */}
                <div className="border-t border-b border-gray-200 py-3 md:py-4">
                  <div className="flex gap-2 md:gap-4 mb-3 md:mb-4 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
                    {['Description', 'Details', 'Reviews'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab.toLowerCase() as any)}
                        className={`px-4 md:px-6 py-2 md:py-2.5 rounded-lg md:rounded-xl font-semibold transition-all duration-300 whitespace-nowrap text-sm md:text-base flex-shrink-0 touch-manipulation ${
                          activeTab === tab.toLowerCase()
                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-emerald-50'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="text-sm md:text-base text-gray-700 leading-relaxed max-h-64 md:max-h-80 overflow-y-auto">
                    {activeTab === 'description' && (
                      <p>{product.description || 'Premium quality product with authentic taste and natural ingredients.'}</p>
                    )}
                    {activeTab === 'details' && (
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <i className="ri-checkbox-circle-fill text-emerald-600"></i>
                          100% Natural Ingredients
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-checkbox-circle-fill text-emerald-600"></i>
                          No Artificial Preservatives
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-checkbox-circle-fill text-emerald-600"></i>
                          Premium Quality Guaranteed
                        </li>
                      </ul>
                    )}
                    {activeTab === 'reviews' && (
                      <div className="space-y-4">
                        {/* Add Review Button */}
                        {!showReviewForm && (
                          <button
                            onClick={() => setShowReviewForm(true)}
                            className="w-full px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-emerald-500/50 cursor-pointer flex items-center justify-center gap-2 touch-manipulation"
                          >
                            <i className="ri-add-line text-xl"></i>
                            Write a Review
                          </button>
                        )}

                        {/* Review Form */}
                        {showReviewForm && (
                          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 space-y-3 border-2 border-emerald-200">
                            <div className="flex items-center justify-between">
                              <h4 className="font-bold text-gray-900 text-base">Write Your Review</h4>
                              <button
                                onClick={() => setShowReviewForm(false)}
                                className="w-8 h-8 bg-white hover:bg-gray-100 rounded-lg flex items-center justify-center transition-all cursor-pointer touch-manipulation"
                              >
                                <i className="ri-close-line text-lg text-gray-600"></i>
                              </button>
                            </div>

                            {/* Name Input */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name</label>
                              <input
                                type="text"
                                value={newReview.userName}
                                onChange={(e) => setNewReview({ ...newReview, userName: e.target.value })}
                                placeholder="Enter your name"
                                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-all text-sm"
                              />
                            </div>

                            {/* Rating Input */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Rating</label>
                              <div className="flex items-center gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <button
                                    key={star}
                                    onClick={() => setNewReview({ ...newReview, rating: star })}
                                    className="cursor-pointer touch-manipulation transition-transform hover:scale-110"
                                  >
                                    <i className={`${star <= newReview.rating ? 'ri-star-fill' : 'ri-star-line'} text-2xl ${star <= newReview.rating ? 'text-amber-500' : 'text-gray-300'}`}></i>
                                  </button>
                                ))}
                                <span className="ml-2 text-sm font-semibold text-gray-700">({newReview.rating}/5)</span>
                              </div>
                            </div>

                            {/* Comment Input */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Your Review</label>
                              <textarea
                                value={newReview.comment}
                                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                                placeholder="Share your experience with this product..."
                                rows={3}
                                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 transition-all text-sm resize-none"
                              />
                            </div>

                            {/* Submit Button */}
                            <button
                              onClick={handleSubmitReview}
                              className="w-full px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-emerald-500/50 cursor-pointer flex items-center justify-center gap-2 touch-manipulation"
                            >
                              <i className="ri-send-plane-fill text-lg"></i>
                              Submit Review
                            </button>
                          </div>
                        )}

                        {/* Reviews List */}
                        <div className="space-y-3 mt-4">
                          {reviews.map((review) => (
                            <div key={review.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <i className="ri-user-line text-emerald-600 text-sm"></i>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-900 text-sm">{review.userName}</p>
                                    <div className="flex items-center gap-1">
                                      {[...Array(5)].map((_, i) => (
                                        <i
                                          key={i}
                                          className={`${i < review.rating ? 'ri-star-fill text-amber-500' : 'ri-star-line text-gray-300'} text-xs`}
                                        ></i>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                                {review.verified && (
                                  <div className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">
                                    <i className="ri-checkbox-circle-fill text-xs"></i>
                                    <span className="text-[10px] font-semibold">Verified</span>
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-gray-700 leading-relaxed mb-1">{review.comment}</p>
                              <p className="text-[10px] text-gray-500">{new Date(review.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quantity Selector - Mobile optimized */}
                <div>
                  <label className="block text-sm md:text-base font-semibold text-gray-700 mb-2 md:mb-3">
                    Quantity
                  </label>
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="flex items-center gap-2 md:gap-3 bg-gray-100 rounded-xl md:rounded-2xl p-1 md:p-2">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 md:w-12 md:h-12 bg-white hover:bg-emerald-600 hover:text-white text-gray-700 rounded-lg md:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer font-bold shadow-md touch-manipulation"
                        aria-label="Decrease quantity"
                      >
                        <i className="ri-subtract-line text-lg md:text-xl"></i>
                      </button>
                      <span className="w-12 md:w-16 text-center font-bold text-gray-900 text-lg md:text-xl">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 md:w-12 md:h-12 bg-white hover:bg-emerald-600 hover:text-white text-gray-700 rounded-lg md:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer font-bold shadow-md touch-manipulation"
                        aria-label="Increase quantity"
                      >
                        <i className="ri-add-line text-lg md:text-xl"></i>
                      </button>
                    </div>
                    <div className="flex-1">
                      <div className="text-xl md:text-2xl font-bold text-gray-900">
                        Total: <span className="text-emerald-600">{formatPrice(product.price * quantity)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - Mobile optimized */}
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 pt-4 md:pt-6">
                  <button
                    onClick={handleAddToCart}
                    disabled={product.soldOut}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                      product.soldOut
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white hover:from-teal-600 hover:to-emerald-700 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    {product.soldOut ? (
                      <>
                        <i className="ri-close-circle-line text-2xl"></i>
                        {t('outOfStock')}
                      </>
                    ) : (
                      <>
                        <i className="ri-shopping-cart-line text-2xl"></i>
                        {t('addToCart')}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {}}
                    className="px-6 md:px-8 py-3 md:py-4 bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-xl md:rounded-2xl font-bold text-base md:text-lg transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap touch-manipulation"
                    aria-label="Add to Wishlist"
                  >
                    <i className="ri-heart-line text-xl md:text-2xl"></i>
                  </button>
                </div>

                {/* Trust Badges - Mobile optimized */}
                <div className="grid grid-cols-3 gap-2 md:gap-4 pt-4 md:pt-6 border-t border-gray-200">
                  {[
                    { icon: 'ri-shield-check-line', text: 'Secure', color: 'from-emerald-500 to-teal-600' },
                    { icon: 'ri-truck-line', text: 'Fast Ship', color: 'from-blue-500 to-cyan-600' },
                    { icon: 'ri-arrow-go-back-line', text: 'Returns', color: 'from-purple-500 to-pink-600' }
                  ].map((badge, index) => (
                    <div key={index} className="text-center">
                      <div className={`w-10 h-10 md:w-12 md:h-12 mx-auto mb-1 md:mb-2 bg-gradient-to-br ${badge.color} rounded-lg md:rounded-xl flex items-center justify-center shadow-lg`}>
                        <i className={`${badge.icon} text-white text-lg md:text-xl`}></i>
                      </div>
                      <p className="text-[10px] md:text-xs font-semibold text-gray-600">{badge.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductQuickView;
